import React, { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'

import { Check16, Edit16 } from '@makyoapp/makyo-icon/icon'
import { Button, ButtonVariant, SimpleInput, SimpleInputProps } from '@makyoapp/makyo-ui'

import { cleanUpString } from '@/shared/utils'

export type RowProps = {
  type?: SimpleInputProps['type']
  className?: string | ((isEdit: boolean) => string)
  editBtnClassName?: string | ((isEdit: boolean) => string)
  id?: string
  label: string
  value: string
  canEdit?: boolean
  onEdit?: (newValue: string) => void
  validate?: (newValue: string, setError: (newError: string) => void) => boolean
  defaultSize?: boolean
  charConstraints?: {
    min: number
    max: number
  }
}

/**
 * the row label
 *
 * @param label
 * @constructor
 */
export function RowLabel({ label }: { label: string }) {
  return <div className='text-m-sm uppercase text-light'>{label}</div>
}

/**
 * Show user profile value in row, used to display:
 * - name
 * - email
 */
export function RowValue({
  className,
  editBtnClassName,
  defaultSize = true,
  id,
  type,
  value,
  onEdit,
  canEdit,
  validate,
  charConstraints,
}: Omit<RowProps, 'label'>) {
  const [editable, setEditable] = useState<boolean>(false)
  const [val, setVal] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState('')

  const showCounter = type === 'text' && !!charConstraints

  useEffect(() => {
    if (editable) {
      inputRef.current?.focus()
    }
  }, [editable])

  const handleSubmit = () => {
    const sameAsBefore = val === value
    const inputCharCount = val.trim().length
    const inputValid = charConstraints
      ? inputCharCount >= (charConstraints?.min ?? 0) && inputCharCount < (charConstraints?.max ?? 0)
      : !!inputCharCount

    if (sameAsBefore || !inputValid) {
      setEditable(false)
      setVal(value)
      return
    }

    if (validate) {
      if (validate(val, setError)) {
        setEditable(!editable)
        onEdit && onEdit(val)
      }
    } else {
      setEditable(!editable)
      onEdit && onEdit(val)
    }
  }

  useEffect(() => {
    let timeout: NodeJS.Timeout

    if (!!val) {
      timeout = setTimeout(() => {
        setVal(cleanUpString(val))
      }, 1000)
    }

    return () => {
      clearTimeout(timeout)
    }
  }, [val])

  return (
    <div
      className={clsx(
        defaultSize && 'text-base',
        editable ? 'inline-flex gap-2 items-start' : 'inline-flex items-center gap-2',
        typeof className === 'function' ? className(editable) : className
      )}
    >
      {!editable && <div>{value}</div>}

      {canEdit && onEdit && (
        <>
          <div className={clsx(showCounter && '[&_.mk-input-desc]:mt-0', editable ? 'block' : 'hidden')}>
            <SimpleInput
              id={id}
              type={type}
              error={error}
              className=''
              ref={inputRef}
              value={val === 'N/A' ? '' : val}
              onChange={(e) => {
                setError('')
                /*const val = e.currentTarget.value.replaceAll(
                  /[^!@#$%^&*()-_=\[\]{}\\\/:;'"`~><?]+/gim,
                  ''
                )*/
                let val = e.currentTarget.value.replaceAll(/[^A-Z0-9.,'\s]+/gim, '')
                val = val.replaceAll(/^[.,']/gim, '')
                setVal(val)
              }}
              outlined={true}
              autoFocus={true}
              onKeyUp={(e) => {
                if (e.key === 'Escape') {
                  setEditable(!editable)
                } else if (e.key === 'Enter' && val !== value) {
                  handleSubmit()
                }
              }}
              withCounterConfig={charConstraints}
            />
          </div>

          <Button
            id={`${id}-btn`}
            variant={ButtonVariant.Ghost}
            noPadding
            squared
            className={clsx(
              editable && 'edit-mode',
              typeof editBtnClassName === 'function' ? editBtnClassName(editable) : editBtnClassName
            )}
            onClick={() => {
              if (!editable) {
                setEditable(true)
              } else {
                handleSubmit()
              }
            }}
          >
            {!editable ? <Edit16 /> : <Check16 />}
          </Button>
        </>
      )}
    </div>
  )
}

/**
 * field to wrap row label and value
 *
 * @param children
 * @constructor
 */
export function RowField({ children }: { children: React.ReactNode }) {
  return <div className={clsx('flex-c mt-1')}>{children}</div>
}
